'use client';
import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';

import styled from '@benzinga/themetron';
import { Button, Icon, Sparkline } from '@benzinga/core-ui';
import { QuoteFreeAlertButton } from '../QuoteFreeAlertButton';
import { AddToWatchlist } from '@benzinga/watchlist-ui';

import { useIsUserLoggedIn } from '@benzinga/user-context';
import { SessionContext } from '@benzinga/session-context';
import { getSessionSingleton } from '@benzinga/session';

import { Candle, ChartManager } from '@benzinga/chart-manager';
import { StockReportsManager } from '@benzinga/stock-reports-manager';
import { BasicNewsManager } from '@benzinga/basic-news-manager';
import { News } from '@benzinga/basic-news-manager';

import { formatPercentage, formatPrice, abbreviateNumber } from '@benzinga/utils';

import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight';
import { faCaretUp } from '@fortawesome/pro-light-svg-icons/faCaretUp';
import { faCaretDown } from '@fortawesome/pro-light-svg-icons/faCaretDown';

import { TickerLink } from '../TickerLink';
import { GetReportButton } from '../GetReportButton';
import { PublicDotComLogo } from '@benzinga/valentyn-icons';
import { EdgeRanking } from '../EdgeRanking';
import { RankingDetail } from '@benzinga/quotes-manager';

export interface TickerCardProps {
  affiliateLink?: string;
  isCrypto?: boolean;
  onPressTicker?: (ticker: string) => void;
  quote: TickerCardQuote;
  rankingData?: RankingDetail;
}

export interface TickerCardQuote {
  bzExchange?: string;
  change: number;
  changePercent: number;
  dayHigh: number;
  dayLow: number;
  fiftyTwoWeekHigh?: number;
  fiftyTwoWeekLow?: number;
  name: string;
  price: number;
  symbol: string;
  volume: number;
  marketCap?: number;
}

export const TickerCard: React.FC<TickerCardProps> = ({
  affiliateLink,
  isCrypto = false,
  onPressTicker,
  quote,
  rankingData,
}) => {
  const [chart, setChart] = useState<number[] | null>(null);
  const [news, setNews] = useState<News[]>([]);
  const [isLoadingChart, setIsLoadingChart] = useState<boolean>(false);
  const [isLoadingNews, setIsLoadingNews] = useState<boolean>(false);
  const [showGetReport, setShowGetReport] = useState(false);
  const isLoggedIn = useIsUserLoggedIn();

  const chartUrl = `https://www.benzinga.com/go/trade-with-public?symbol=${quote?.symbol}`;
  const session = getSessionSingleton();
  const chartManager = session.getManager(ChartManager);

  const [showRanking, setShowRanking] = React.useState(false);

  useEffect(() => {
    if (isLoggedIn) {
      setShowRanking(true);
    }
  }, [isLoggedIn]);

  React.useEffect(() => {
    const getSupportedTickers = async () => {
      const result = await session.getManager(StockReportsManager).isSupportedTicker(quote.symbol, { months: 1.5 });
      setShowGetReport(result);
      return result;
    };

    getSupportedTickers();
  }, [quote?.symbol, session]);

  React.useEffect(() => {
    const loadQuoteChart = (ticker: string) => {
      setIsLoadingChart(true);

      chartManager
        .getChart({
          from: '5m',
          interval: '1d',
          symbol: ticker,
        })
        .then(res => {
          if (res?.ok && res.ok?.candles) {
            let min: number = res.ok.candles[0].close;
            const chartData = res.ok.candles
              .map((candle: Candle) => {
                if (candle.close < min) {
                  min = candle.close;
                }
                return candle.close;
              })
              .map((close: number) => {
                return close / min - 1;
              });
            setChart(chartData);
            setIsLoadingChart(false);
          }
        });
    };

    const loadCryptoQuoteBars = (ticker: string) => {
      setIsLoadingChart(true);

      const symbol = ticker.split('/USD')[0];
      const zone = 'America/Detroit';
      const now = DateTime.now().setZone(zone);
      const date = now.toISODate();

      const from = now.minus({ months: 5 }).toISODate();
      const to = date;

      chartManager.getCryptoChart(`${symbol}USD`, from, to, 1, 'hour').then(res => {
        if (res?.ok && res.ok?.results) {
          let min: number = res.ok.results[0].close;
          const chartData = res.ok.results
            .map(candle => {
              if (candle.close < min) {
                min = candle.close;
              }
              return candle.close;
            })
            .map((close: number) => {
              return close / min - 1;
            });
          setChart(chartData);
          setIsLoadingChart(false);
        }
      });
    };

    const loadNews = (ticker: string) => {
      setIsLoadingNews(true);
      const basicNewsManager = session.getManager(BasicNewsManager);
      basicNewsManager.fetchNodes({ pageSize: 3, symbols: [ticker] }).then(res => {
        if (res?.ok) {
          setNews(res.ok);
          setIsLoadingNews(false);
        }
      });
    };

    if (!chart && !isLoadingChart) {
      if (quote?.symbol?.includes('/USD')) {
        loadCryptoQuoteBars(quote?.symbol);
      } else {
        loadQuoteChart(quote?.symbol);
      }
    }

    if (!news.length && !isLoadingNews) {
      loadNews(quote?.symbol);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.symbol]);

  return (
    <TickerCardWrapper className="ticker-card-wrapper">
      <div className="ticker-card-container">
        <div className="ticker-card-top-section-container">
          <div className="ticker-card-top-section-container-left">
            <div>
              <div className="quote-symbol-anchor-wrapper">
                <TickerLink onPressTicker={onPressTicker} symbol={quote?.symbol}>
                  {quote?.bzExchange ? <span>{quote?.bzExchange}:</span> : ''} {quote?.symbol}
                </TickerLink>
              </div>
              <TickerLink
                className="ticker-link-quote-name whitespace-normal"
                onPressTicker={onPressTicker}
                symbol={quote?.symbol}
              >
                <span className="quote-name">{quote?.name}</span>
              </TickerLink>
            </div>
          </div>
          <div className="ticker-card-top-section-container-right">
            {quote ? (
              <div className="flex flex-row gap-2 justify-end">
                <div
                  className={`quote-price-container ${quote?.changePercent > 0 ? 'text-green' : ''}${
                    quote?.changePercent < 0 ? 'text-red' : ''
                  }`}
                >
                  {quote.price ? <div>${formatPrice(quote.price)}</div> : null}
                  <div className="quote-change">
                    {quote?.change !== 0 && (
                      <div>
                        <Icon className="change-icon" icon={quote?.change > 0 ? faCaretUp : faCaretDown} />
                      </div>
                    )}
                    <span style={{ marginRight: '0.25rem' }}>{formatPrice(quote?.change)}</span>
                    {typeof quote?.changePercent === 'number' ? (
                      <span>
                        (
                        {quote?.changePercent > 0
                          ? `+${formatPercentage(quote?.changePercent)}%`
                          : `${formatPercentage(quote?.changePercent)}%`}
                        )
                      </span>
                    ) : null}

                    {/* {quote?.sessionType === 'AFTER_MARKET' && (
                      <div>
                        <div className="text-green-500 font-bold">
                          ${quote?.afterMarketPrice}
                        </div>
                      </div>
                    )} */}
                  </div>
                </div>
                {isLoggedIn && <AddToWatchlist symbol={quote?.symbol} variant="compact" />}
              </div>
            ) : null}
          </div>

          <div className="free-alerts-wrapper">
            {!isLoggedIn && (
              <QuoteFreeAlertButton
                link={`https://www.benzinga.com/profile/portfolio/?add_ticker=${quote?.symbol}`}
                size="sm"
              />
            )}
            {showGetReport && <GetReportButton size="sm" ticker={quote?.symbol} />}
          </div>
        </div>
        <div className="relative p-2">
          {rankingData && (
            <EdgeRanking
              {...rankingData}
              adType="tickerhover"
              layout="compact"
              showRanking={showRanking}
              symbol={quote?.symbol}
              variant="light"
            />
          )}
        </div>
        <a className="ticker-card-sparkline-container" href={chartUrl} rel="noreferrer" target="_blank">
          {chart && <Sparkline color="#08A700" data={chart} height={60} width={300} />}
          <StyledPublicDotComLogo className="ml-auto pr-2" />
        </a>
        <div className="ticker-card-middle-section-container">
          <div>
            {isCrypto && quote?.marketCap && (
              <div>
                <span className="middle-section-header">Market Cap:</span>
                <span>{abbreviateNumber(quote.marketCap)}</span>
              </div>
            )}
            <div>
              <span className="middle-section-header">Vol:</span>
              <span>{abbreviateNumber(quote?.volume)}</span>
            </div>
            <div>
              <span className="middle-section-header">Day range:</span>
              <span>
                <span>{quote?.dayLow}</span> - <span>{quote?.dayHigh}</span>
              </span>
            </div>
            {!isCrypto && (
              <div>
                <span className="middle-section-header">52 week:</span>
                <span>
                  <span>{quote?.fiftyTwoWeekLow}</span> - <span>{quote?.fiftyTwoWeekHigh}</span>
                </span>
              </div>
            )}
          </div>
        </div>
        {Boolean(news?.length) && (
          <div className="recent-news-container">
            <h3 className="recent-news-header">Recent News</h3>
            <ul>
              {news.map((newsItem, n: number) => (
                <li key={`news-${n}`}>
                  <a href={newsItem.url} rel="noopener noreferrer" target="_blank">
                    {newsItem?.title && newsItem?.title.length > 44
                      ? `${newsItem.title.substr(0, 45)}...`
                      : newsItem.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        {affiliateLink && (
          <div className="affiliate-link-wrapper">
            <a href={affiliateLink} rel="noreferrer" target="_blank">
              <Button variant="primary">Start Investing</Button>
            </a>
          </div>
        )}
        <TickerLink className="more-about" onPressTicker={onPressTicker} symbol={quote?.symbol}>
          More about {quote?.symbol}
          <Icon className="text-blue-500" icon={faArrowRight} />
        </TickerLink>
        {chartUrl && (
          <TickerLink
            className="more-about public-com-link"
            href={chartUrl}
            onPressTicker={onPressTicker}
            symbol={quote?.symbol}
          >
            <span className="bg-blue-500 hover:bg-blue-600 rounded py-1 px-2 !text-white">Trade with Public</span>
          </TickerLink>
        )}
      </div>
    </TickerCardWrapper>
  );
};

export const StyledPublicDotComLogo = styled(PublicDotComLogo)`
  width: 69px;
  height: 18px;
`;

export const FreeAlertsLink = styled.a`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 0.25rem;
  padding: 0.15rem 0.25rem;
  font-size: 0.75rem;
  line-height: 18px;
  margin: 0.4rem 0;
`;

export const TradeWithPublic = styled.span`
  color: #0038ff;
`;

export const TickerCardWrapper = styled.div`
  &.ticker-card-wrapper {
    text-align: left;
    a {
      text-decoration: none;
    }
    .ticker-link-quote-name {
      line-height: 1;
    }
    .ticker-card-container {
      background: ${({ theme }) => theme.colors.background};
      display: flex;
      flex-direction: column;
      max-width: 320px;
      overflow: hidden;
      border-radius: ${({ theme }) => theme.borderRadius.sm};
      color: ${({ theme }) => theme.colors.foreground};
      border: solid 1px ${({ theme }) => theme.colors.border};

      .get-report-button {
        white-space: nowrap;
      }

      .free-alerts-wrapper {
        margin-top: 0.4rem;
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 0px 8px 8px 8px;
        line-height: 1;

        .logo-wrapper {
          padding-right: 0.5rem;
          right: 0;
          position: absolute;
        }
      }

      .ticker-card-top-section-container {
        border-bottom: 1px solid rgba(57, 81, 115, 0.1);
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));

        .ticker-card-top-section-container-left {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem;

          > div {
            display: flex;
            flex-direction: column;

            .quote-symbol-anchor-wrapper {
              display: block;
              flex-wrap: wrap;
              font-weight: 700;
              align-items: center;
              color: ${({ theme }) => theme.colors.foreground};
              font-size: ${({ theme }) => theme.fontSize.lg};

              a {
                color: ${({ theme }) => theme.colors.foreground};
                margin-right: 0.375rem;
              }
            }

            .quote-name {
              color: ${({ theme }) => theme.colors.foreground};
              font-size: 0.875rem;
              line-height: 1.25rem;
            }
          }
        }

        .ticker-card-top-section-container-right {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem;
          padding-top: 0.75rem;

          > div {
            text-align: right;
            width: 100%;

            .quote-price-container {
              display: inline-flex;
              flex-direction: column;
              align-items: flex-end;
              font-size: 1.25rem;
              line-height: 1.75rem;

              &.text-green {
                color: ${({ theme }) => theme.colorPalette.green500};
              }

              &.text-red {
                color: ${({ theme }) => theme.colorPalette.red500};
              }

              > div:nth-of-type(1) {
                margin-top: -0.5rem;
                font-weight: 700;
              }

              .quote-change {
                font-size: 0.65rem;
                margin-top: -0.75rem;
                margin-bottom: -0.75rem;
                font-weight: 700;
                display: inline-flex;
                align-items: center;
                padding-top: 0.25rem;
                padding-bottom: 0.25rem;
                border-radius: 0.75rem;
                text-align: right;

                .change-icon {
                  margin-top: 0.125rem;
                  font-size: 0.75rem;
                  line-height: 1rem;
                }
              }
            }
          }
        }
      }

      .ticker-card-middle-section-container {
        display: grid;
        flex-direction: column;
        font-weight: 600;
        padding: 12px 8px;
        color: ${({ theme }) => theme.colors.foregroundInactive};

        > div:first-child {
          font-size: 0.75rem;
          line-height: 1rem;
          grid-column: span 2 / span 2;
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 4px;
          justify-content: space-between;

          > div {
            margin-bottom: 5px;

            &:last-of-type {
              margin-bottom: 0;
            }
          }

          .middle-section-header {
            display: inline-block;
            width: 5rem;

            + span {
              color: ${({ theme }) => theme.colors.foregroundInactive};
            }
          }
        }
      }
      .ticker-card-sparkline-container {
        background-color: ${({ theme }) => theme.colors.backgroundInactive};
        display: grid;
        overflow: hidden;
        min-width: 6rem;
        border-radius: 0.25rem;
        border-bottom: 1px solid rgba(57, 81, 115, 0.1);
        padding-top: 1rem;
      }

      .recent-news-container {
        display: flex;
        flex-direction: column;
        border-top: 1px solid rgba(57, 81, 115, 0.1);
        padding: 0.5rem;

        .recent-news-header {
          font-weight: 600;
          color: ${({ theme }) => theme.colors.foreground};
          font-size: 0.875rem;
          line-height: 1.25rem;
          text-transform: uppercase;
          margin: 0;
          margin-bottom: 0.25rem;
        }

        ul {
          margin: 0;
          font-size: 0.75rem;
          line-height: 1rem;
          color: ${({ theme }) => theme.colors.foreground};
          padding: 0;

          li {
            list-style: none;
            margin: 0;
            margin-bottom: 5px;

            a {
              color: ${({ theme }) => theme.colors.foreground};
            }

            &:last-of-type {
              margin-bottom: 0;
            }

            &:hover {
              text-decoration: underline;
            }
          }
        }
      }

      .affiliate-link-wrapper {
        display: flex;
        width: 100%;
        padding: 0.5rem;
      }

      .more-about {
        /* text-blue-500 text-sm flex font-semibold items-center justify-between p-2 border-t border-gray-400 border-opacity-20 */
        color: ${({ theme }) => theme.colorPalette.blue500};
        font-size: ${({ theme }) => theme.fontSize.sm};
        display: flex;
        font-weight: ${({ theme }) => theme.fontWeight.semibold};
        align-items: center;
        justify-content: space-between;
        padding: 0.5rem;
        border-top: solid 1px ${({ theme }) => theme.colors.border};

        &.public-com-link {
          font-size: ${({ theme }) => theme.fontSize.md};
          justify-content: flex-end;
        }
      }
    }
  }
`;
